import type { ReportStrategyPolicyVersion } from "../types/reports";
import {
  isPolicyPayloadV1,
  isPolicyTreeBundleV1,
  isPolicyTreeV2,
} from "../types/tradePolicy";

/** Counts of combine-relevant segment kinds across one or more policy trees. */
export type CombineSegmentKindCounts = {
  keep: number;
  veto: number;
  management: number;
};

export type CombineStackVisibility = {
  showKeepCombine: boolean;
  showVetoCombine: boolean;
  /** Only Exit Placement / management segments — Keep/Remove stacks do not apply. */
  managementOnly: boolean;
  hasEntry: boolean;
  hasManagement: boolean;
  counts: CombineSegmentKindCounts;
};

const EMPTY_COUNTS: CombineSegmentKindCounts = { keep: 0, veto: 0, management: 0 };

function addCounts(
  a: CombineSegmentKindCounts,
  b: CombineSegmentKindCounts
): CombineSegmentKindCounts {
  return {
    keep: a.keep + b.keep,
    veto: a.veto + b.veto,
    management: a.management + b.management,
  };
}

function countsFromAction(action: unknown): CombineSegmentKindCounts {
  const raw = typeof action === "string" ? action.trim().toLowerCase() : "";
  if (raw === "keep_only" || raw === "keep-only" || raw === "keep only") {
    return { keep: 1, veto: 0, management: 0 };
  }
  if (raw === "veto" || raw === "remove") {
    return { keep: 0, veto: 1, management: 0 };
  }
  return EMPTY_COUNTS;
}

/**
 * Classify a single policy tree for combine stack UI (Keep / Remove vs management).
 * Bundles count each segment; v2 rules are all management.
 */
export function countCombineSegmentKinds(policyTree: unknown): CombineSegmentKindCounts {
  if (policyTree == null) return EMPTY_COUNTS;

  if (isPolicyTreeV2(policyTree)) {
    const n = policyTree.rules?.length ?? 0;
    return n > 0 ? { keep: 0, veto: 0, management: n } : EMPTY_COUNTS;
  }

  if (isPolicyTreeBundleV1(policyTree)) {
    let totals = EMPTY_COUNTS;
    for (const segment of policyTree.segments ?? []) {
      const kind =
        typeof segment.segment_kind === "string"
          ? segment.segment_kind.trim().toLowerCase()
          : "";
      if (kind === "management") {
        totals = addCounts(totals, { keep: 0, veto: 0, management: 1 });
        continue;
      }
      // entry_filter or omitted — use action
      totals = addCounts(totals, countsFromAction(segment.action));
    }
    return totals;
  }

  if (isPolicyPayloadV1(policyTree)) {
    return countsFromAction(policyTree.action);
  }

  if (typeof policyTree === "object" && !Array.isArray(policyTree)) {
    const record = policyTree as Record<string, unknown>;
    if ("action" in record && "root" in record) {
      return countsFromAction(record.action);
    }
  }

  return EMPTY_COUNTS;
}

export function sumCombineSegmentKindCounts(
  trees: unknown[]
): CombineSegmentKindCounts {
  return trees.reduce<CombineSegmentKindCounts>(
    (acc, tree) => addCounts(acc, countCombineSegmentKinds(tree)),
    EMPTY_COUNTS
  );
}

export function combineStackVisibilityFromCounts(
  counts: CombineSegmentKindCounts
): CombineStackVisibility {
  const hasEntry = counts.keep + counts.veto > 0;
  const hasManagement = counts.management > 0;
  return {
    showKeepCombine: counts.keep >= 2,
    showVetoCombine: counts.veto >= 2,
    managementOnly: hasManagement && !hasEntry,
    hasEntry,
    hasManagement,
    counts,
  };
}

export function combineStackVisibilityFromPolicyTrees(
  trees: unknown[]
): CombineStackVisibility {
  return combineStackVisibilityFromCounts(sumCombineSegmentKindCounts(trees));
}

export function combineStackVisibilityFromPolicyVersions(
  versions: ReportStrategyPolicyVersion[]
): CombineStackVisibility {
  return combineStackVisibilityFromPolicyTrees(versions.map((v) => v.policy_tree));
}

/**
 * Workspace fallback when policy trees are not loaded yet.
 * `management` = Exit Placement lineage; `entry` = Regime / other report policies.
 * Unknown sources are treated as entry so Keep/Remove stay available when unsure.
 */
export function combineStackVisibilityFromSourceKinds(
  kinds: Array<"management" | "entry" | "unknown">
): CombineStackVisibility {
  let management = 0;
  let entry = 0;
  for (const kind of kinds) {
    if (kind === "management") management += 1;
    else entry += 1; // entry + unknown
  }
  // Without keep vs veto, only hide stacks for pure management; otherwise
  // show both when ≥2 entry-ish sources (operands are no-ops with <2 of a kind on BE).
  const counts: CombineSegmentKindCounts = {
    keep: entry,
    veto: entry,
    management,
  };
  if (management > 0 && entry === 0) {
    return combineStackVisibilityFromCounts({ keep: 0, veto: 0, management });
  }
  return {
    showKeepCombine: entry >= 2,
    showVetoCombine: entry >= 2,
    managementOnly: false,
    hasEntry: entry > 0,
    hasManagement: management > 0,
    counts,
  };
}
